import { useCallback, useEffect, useState } from "react";
import { Loader2, Trash2, AlertTriangle, Search, Ban, CheckCircle2 } from "lucide-react";
import { AdminShell } from "../components/AdminShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";

const COUNT_LABELS = {
  groups: "Grupos", attendees: "Adultos", children: "Niños", guardians: "Tutores autorizados",
  payments: "Pagos individuales", group_payments: "Pagos de grupo", entitlements: "Derechos de acceso",
  workshop_registrations: "Inscripciones a talleres", check_ins: "Check-ins",
  child_care_checkins: "Movimientos de cuidado", incidents: "Incidencias",
};

const ToolsContent = () => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmation, setConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [result, setResult] = useState(null);
  const [folio, setFolio] = useState("");
  const [impact, setImpact] = useState(null);
  const [reason, setReason] = useState("");
  const [removing, setRemoving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/data-tools/test-data/preview")
      .then((r) => setPreview(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const purge = async () => {
    if (deleting) return;
    setDeleting(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/data-tools/test-data/delete", { confirmation });
      setResult(data.deleted);
      setConfirmation("");
      toast.success("Datos de prueba eliminados");
      load();
    } catch (err) {
      toast.error(apiError(err, "No se pudo completar la limpieza."));
    } finally {
      setDeleting(false);
    }
  };

  const loadImpact = async (e) => {
    e.preventDefault();
    setImpact(null);
    try {
      const { data } = await api.get("/admin/data-tools/group-impact", { params: { group_folio: folio } });
      setImpact(data);
    } catch (err) {
      toast.error(apiError(err, "Grupo no encontrado."));
    }
  };

  const removeGroup = async () => {
    if (!impact || removing) return;
    setRemoving(true);
    try {
      const { data } = await api.post(`/admin/groups/${impact.group.id}/remove`, { reason });
      toast.success(data.action === "deleted" ? "Grupo eliminado físicamente" : "Grupo cancelado y accesos revocados");
      setImpact(null);
      setFolio("");
      setReason("");
      load();
    } catch (err) {
      toast.error(apiError(err, "No se pudo procesar la acción."));
    } finally {
      setRemoving(false);
    }
  };

  if (loading || !preview) {
    return <div className="flex items-center gap-2 py-16 text-hz-navy"><Loader2 className="h-5 w-5 animate-spin" /> Cargando…</div>;
  }

  return (
    <div className="space-y-8" data-testid="data-tools">
      <section className="rounded-2xl border border-hz-error/30 bg-white p-6" data-testid="test-data-section">
        <h2 className="flex items-center gap-2 text-lg font-bold text-hz-navy"><Trash2 className="h-5 w-5 text-hz-error" /> Limpieza de datos de prueba</h2>
        {!preview.enabled ? (
          <p className="mt-3 rounded-lg border border-hz-warning/30 bg-hz-warning/5 px-4 py-3 text-sm font-medium text-hz-ink" data-testid="test-data-disabled">
            Esta herramienta está desactivada en este ambiente (ALLOW_TEST_DATA_RESET=false).
          </p>
        ) : (
          <>
            <p className="mt-2 text-sm text-hz-ink/70">
              Elimina únicamente registros marcados como datos de prueba (is_test_data=true). Los catálogos oficiales, usuarios y configuración no se tocan.
            </p>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="test-data-counts">
              {Object.entries(preview.counts).map(([k, v]) => (
                <div key={k} className="rounded-xl border border-hz-gray/40 bg-hz-bg p-3 text-center" data-testid={`count-${k}`}>
                  <p className="text-xl font-extrabold text-hz-navy">{v}</p>
                  <p className="text-xs font-semibold text-hz-ink/60">{COUNT_LABELS[k] || k}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 space-y-3">
              <Label className="text-sm font-semibold text-hz-ink">Escribe exactamente <span className="font-mono font-bold text-hz-error">ELIMINAR DATOS DE PRUEBA</span> para confirmar</Label>
              <Input value={confirmation} onChange={(e) => setConfirmation(e.target.value)} data-testid="test-data-confirmation-input" placeholder="ELIMINAR DATOS DE PRUEBA" />
              <Button onClick={purge} disabled={confirmation !== "ELIMINAR DATOS DE PRUEBA" || deleting} data-testid="test-data-delete-btn"
                className="bg-hz-error font-bold text-white hover:bg-hz-error/90 disabled:opacity-50">
                {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <AlertTriangle className="mr-2 h-4 w-4" />}
                Eliminar datos de prueba
              </Button>
            </div>
            {result && (
              <div className="mt-4 rounded-xl border border-hz-success/30 bg-hz-success/5 p-4" data-testid="test-data-result">
                <p className="flex items-center gap-2 text-sm font-bold text-hz-success"><CheckCircle2 className="h-4 w-4" /> Limpieza completada</p>
                <ul className="mt-2 grid grid-cols-2 gap-1 text-xs text-hz-ink sm:grid-cols-4">
                  {Object.entries(result).map(([k, v]) => <li key={k}>{COUNT_LABELS[k] || k}: <b>{v}</b></li>)}
                </ul>
              </div>
            )}
          </>
        )}
      </section>

      <section className="rounded-2xl border border-hz-gray/40 bg-white p-6" data-testid="group-remove-section">
        <h2 className="flex items-center gap-2 text-lg font-bold text-hz-navy"><Ban className="h-5 w-5 text-hz-navy" /> Eliminación o cancelación controlada de un registro</h2>
        <p className="mt-2 text-sm text-hz-ink/70">
          Los registros de prueba sin operación se eliminan físicamente. Los registros reales con pagos o movimientos solo se cancelan: se revocan sus accesos y se conserva la auditoría.
        </p>
        <form onSubmit={loadImpact} className="mt-4 flex gap-2">
          <Input value={folio} onChange={(e) => setFolio(e.target.value)} placeholder="Folio de grupo (ej. HZ26-GRP-000001)" data-testid="group-impact-input" />
          <Button type="submit" variant="outline" data-testid="group-impact-btn" className="font-bold text-hz-navy"><Search className="h-4 w-4" /></Button>
        </form>
        {impact && (
          <div className="mt-4 space-y-4 rounded-xl border border-hz-gray/40 bg-hz-bg p-4" data-testid="group-impact-result">
            <div className="text-sm">
              <p className="font-bold text-hz-navy">{impact.group.group_folio} {impact.group.group_name ? `— ${impact.group.group_name}` : ""}</p>
              <p className="text-hz-ink/70">Tipo: {impact.group.group_type} · Pago: {impact.group.payment_mode} · Estado: {impact.group.registration_status} · {impact.group.is_test_data ? "Datos de prueba" : "Datos reales"}</p>
            </div>
            <ul className="grid grid-cols-2 gap-2 text-xs text-hz-ink sm:grid-cols-5" data-testid="group-impact-deps">
              <li>Adultos: <b>{impact.dependencies.attendees}</b></li>
              <li>Niños: <b>{impact.dependencies.children}</b></li>
              <li>Pagos aprobados: <b>{impact.dependencies.approved_payments}</b></li>
              <li>Check-ins: <b>{impact.dependencies.check_ins}</b></li>
              <li>Movimientos de niños: <b>{impact.dependencies.child_care_checkins}</b></li>
            </ul>
            <p className={`rounded-lg px-3 py-2 text-sm font-semibold ${impact.can_delete ? "bg-hz-warning/10 text-hz-warning" : "bg-hz-cyan/10 text-hz-blue"}`} data-testid="group-impact-action">
              {impact.can_delete
                ? "Este registro es de prueba y no tiene operación: se ELIMINARÁ físicamente."
                : "Este registro tiene operación o es real: se CANCELARÁ y se revocarán sus accesos (no se borra)."}
            </p>
            <div>
              <Label className="text-sm font-semibold text-hz-ink">Motivo obligatorio *</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="group-remove-reason-input" className="mt-1" placeholder="Ej. Registro duplicado por error de captura" />
            </div>
            <Button onClick={removeGroup} disabled={reason.trim().length < 5 || removing} data-testid="group-remove-confirm-btn"
              className={`${impact.can_delete ? "bg-hz-error" : "bg-hz-navy"} font-bold text-white disabled:opacity-50`}>
              {removing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {impact.can_delete ? "Eliminar registro" : "Cancelar registro y revocar accesos"}
            </Button>
          </div>
        )}
      </section>
    </div>
  );
};

const AdminHerramientasDatos = () => (
  <AdminShell roles={["admin"]} title="Herramientas de datos" testId="admin-data-tools"
    subtitle="Limpieza controlada de datos de prueba y cancelación/eliminación de registros. Todas las acciones quedan en auditoría.">
    {() => <ToolsContent />}
  </AdminShell>
);

export default AdminHerramientasDatos;
